import { v4 as uuidv4 } from "uuid";
import { ref, uploadBytesResumable } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { storage, functionsClient } from "../firebase/firebaseConfig";

/**
 * Upload one or more PDF files to Cloud Storage under a fresh requestId.
 *
 * Each file gets a per-upload `fileId` so two PDFs with the same filename
 * land in distinct paths — `pdf-imports/{userId}/{requestId}/{fileId}/{filename}`.
 * The progress callback is keyed by `fileId`, not filename, so the caller's
 * progress map is collision-free.
 *
 * The returned `paths` are surfaced for UI progress display only — they are
 * NOT sent to the callable function. The backend re-derives the canonical
 * paths from `pdf-imports/{userId}/{requestId}/...` using the authenticated
 * user's uid, so the client cannot smuggle in an arbitrary path.
 *
 * @param {File[]} files
 * @param {string} userId
 * @param {(fileId: string, fraction: number) => void} [onProgress]
 * @returns {Promise<{ requestId: string, paths: string[], fileIds: string[] }>}
 */
export async function uploadPdfsToStorage(files, userId, onProgress, providedFileIds) {
  const requestId = uuidv4();
  const paths = [];
  const fileIds = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    // Caller may supply per-file IDs (e.g. component-generated at selection
    // time so React keys + progress map are stable across renders). Fall
    // back to a fresh ID per file when the caller doesn't care.
    const fileId = (providedFileIds && providedFileIds[i])
      || uuidv4().replace(/-/g, "").slice(0, 12);
    const path = `pdf-imports/${userId}/${requestId}/${fileId}/${file.name}`;
    const fileRef = ref(storage, path);
    await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(fileRef, file);
      task.on(
        "state_changed",
        (snap) => {
          if (onProgress && snap && snap.totalBytes) {
            onProgress(fileId, snap.bytesTransferred / snap.totalBytes);
          }
        },
        reject,
        resolve
      );
    });
    paths.push(path);
    fileIds.push(fileId);
  }
  return { requestId, paths, fileIds };
}

/**
 * Invoke the deployed `parsePurchaseAgreement` callable.
 *
 * Returns the consolidated ParseResponse:
 *   {
 *     status: "ok" | "partial" | "error",
 *     parsedFields: { offerPrice, earnestMoney, closingdate, propertyaddress,
 *                     financingType, financingDeadline, inspection,
 *                     inspectionDays, finalAcceptanceDate, inspectionPeriodEnd },
 *     fieldSources: { [field]: "PA line N" | "Counter line N" | ... },
 *     unmappedFields: string[],
 *     flaggedFields: string[],
 *     manualReviewNotes: string[],
 *     documentSummary: { pa, counter, blocks },
 *     warnings: string[],
 *     errorCode?, errorMessage?,    // present only on status: "error"
 *   }
 *
 * @param {string} requestId
 * @returns {Promise<object>} ParseResponse
 */
export async function callParseFunction(requestId) {
  const callable = httpsCallable(functionsClient, "parsePurchaseAgreement", {
    timeout: 300_000,
  });
  const result = await callable({ requestId });
  return result.data;
}

/**
 * Best-effort geocode using the Google Maps JS SDK if it has been loaded
 * onto `window`. Returns null when the SDK is unavailable or the address
 * does not resolve, so callers can fall back to manual entry.
 *
 * @param {string} address
 * @returns {Promise<{ lat: number, lng: number } | null>}
 */
export async function geocodeAddress(address) {
  if (typeof window === "undefined" || !window.google?.maps?.Geocoder) {
    return null;
  }
  const geocoder = new window.google.maps.Geocoder();
  return new Promise((resolve) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const { lat, lng } = results[0].geometry.location;
        resolve({ lat: lat(), lng: lng() });
      } else {
        resolve(null);
      }
    });
  });
}
